import { useState, useCallback, useMemo } from 'react';
import { SortState, SortDirection } from '../../../../types/datagrid';

interface UseGridSortParams {
  initialSorting?: SortState[];
  onSort?: (sorting: SortState[]) => void;
  maxSorts?: number;
}

export const useGridSort = ({ initialSorting = [], onSort, maxSorts = 3 }: UseGridSortParams) => {
  const [sorting, setSorting] = useState<SortState[]>(initialSorting);

  // Handle single column sort
  const handleSort = useCallback(
    (columnId: string) => {
      setSorting(prevSorting => {
        const existingSort = prevSorting.find(s => s.field === columnId);

        if (!existingSort) {
          // New sort - ascending
          const newSorting = [{ field: columnId, direction: 'asc' as SortDirection, order: 1 }];
          onSort?.(newSorting);
          return newSorting;
        }

        if (existingSort.direction === 'asc') {
          // Change to descending
          const newSorting = [{ field: columnId, direction: 'desc' as SortDirection, order: 1 }];
          onSort?.(newSorting);
          return newSorting;
        }

        // Remove sort (was descending)
        const newSorting: SortState[] = [];
        onSort?.(newSorting);
        return newSorting;
      });
    },
    [onSort]
  );

  // Handle multi-column sort (Shift+Click)
  const handleMultiSort = useCallback(
    (columnId: string) => {
      setSorting(prevSorting => {
        const existingSort = prevSorting.find(s => s.field === columnId);

        if (!existingSort) {
          // Add new sort
          if (prevSorting.length >= maxSorts) {
            // Remove oldest sort and add new one
            const newSorting = [
              ...prevSorting.slice(1).map((s, idx) => ({ ...s, order: idx + 1 })),
              { field: columnId, direction: 'asc' as SortDirection, order: maxSorts },
            ];
            onSort?.(newSorting);
            return newSorting;
          } else {
            // Add new sort
            const newSorting = [
              ...prevSorting,
              { field: columnId, direction: 'asc' as SortDirection, order: prevSorting.length + 1 },
            ];
            onSort?.(newSorting);
            return newSorting;
          }
        }

        if (existingSort.direction === 'asc') {
          // Change to descending
          const newSorting = prevSorting.map(s =>
            s.field === columnId ? { ...s, direction: 'desc' as SortDirection } : s
          );
          onSort?.(newSorting);
          return newSorting;
        }

        // Remove this sort
        const newSorting = prevSorting
          .filter(s => s.field !== columnId)
          .map((s, idx) => ({ ...s, order: idx + 1 }));
        onSort?.(newSorting);
        return newSorting;
      });
    },
    [onSort, maxSorts]
  );

  // Reset all sorting
  const resetSort = useCallback(() => {
    setSorting([]);
    onSort?.([]);
  }, [onSort]);

  // Get sort state for a specific column
  const getColumnSortState = useCallback(
    (columnId: string) => {
      const sort = sorting.find(s => s.field === columnId);
      return {
        direction: sort?.direction || null,
        order: sort?.order || 0,
        isActive: !!sort,
      };
    },
    [sorting]
  );

  // Memoized sort indicators
  const sortIndicators = useMemo(() => {
    return sorting.reduce(
      (acc, sort) => {
        acc[sort.field] = {
          direction: sort.direction,
          order: sort.order,
          isMultiSort: sorting.length > 1,
        };
        return acc;
      },
      {} as Record<string, { direction: SortDirection; order: number; isMultiSort: boolean }>
    );
  }, [sorting]);

  // Sort data utility
  const sortData = useCallback(
    (data: any[]) => {
      if (!sorting.length) return data;

      return [...data].sort((a, b) => {
        for (const sort of sorting.sort((x, y) => x.order - y.order)) {
          const aVal = a[sort.field];
          const bVal = b[sort.field];

          if (aVal === bVal) continue;

          let comparison = 0;

          // Handle null/undefined values
          if (aVal == null && bVal == null) comparison = 0;
          else if (aVal == null) comparison = -1;
          else if (bVal == null) comparison = 1;
          // Handle strings
          else if (typeof aVal === 'string' && typeof bVal === 'string') {
            comparison = aVal.localeCompare(bVal, undefined, {
              numeric: true,
              sensitivity: 'base',
            });
          }
          // Handle numbers
          else if (typeof aVal === 'number' && typeof bVal === 'number') {
            comparison = aVal - bVal;
          }
          // Handle dates
          else if (aVal instanceof Date && bVal instanceof Date) {
            comparison = aVal.getTime() - bVal.getTime();
          }
          // Handle booleans
          else if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
            comparison = aVal === bVal ? 0 : aVal ? 1 : -1;
          }
          // Fallback to string comparison
          else {
            comparison = String(aVal).localeCompare(String(bVal), undefined, {
              numeric: true,
              sensitivity: 'base',
            });
          }

          if (comparison !== 0) {
            return sort.direction === 'desc' ? -comparison : comparison;
          }
        }
        return 0;
      });
    },
    [sorting]
  );

  // Check if any sorts are active
  const hasActiveSorts = sorting.length > 0;

  // Generate sort description for accessibility
  const getSortDescription = useCallback(
    (columnId: string) => {
      const sort = sorting.find(s => s.field === columnId);
      if (!sort) return 'Not sorted';

      const direction = sort.direction === 'asc' ? 'ascending' : 'descending';
      const position = sorting.length > 1 ? ` (${sort.order} of ${sorting.length})` : '';

      return `Sorted ${direction}${position}`;
    },
    [sorting]
  );

  return {
    sorting,
    handleSort,
    handleMultiSort,
    resetSort,
    getColumnSortState,
    sortIndicators,
    sortData,
    hasActiveSorts,
    getSortDescription,

    // For external state updates
    setSorting: useCallback(
      (newSorting: SortState[]) => {
        setSorting(newSorting);
        onSort?.(newSorting);
      },
      [onSort]
    ),
  };
};
